import React, {useState} from "react";
import { Table } from "antd";
import { useSelector } from "react-redux";
import commaNumber from "comma-number";
import _ from "lodash";

const format = commaNumber.bindWith(",", ".");

const CustomerDueTable = ({ printMode = false, authInfo }) => {
  const { report, loading } = useSelector((state) => state.customerDueReport);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };


  const columns = [
    {
      title: "No.",
      render: (_, __, index) => index + 1,
      width: 60
    },
    {
      title: "Voucher No",
      dataIndex: "voucher_no",
      align: "right"
    },
    {
      title: "Bill Amount",
      dataIndex: "total_amount",
      align: "right",
      render: (val) => format(parseFloat(val || 0).toFixed(2))
    },
    {
      title: "Paid Amount Invoice",
      dataIndex: "paid_amount",
      align: "right",
      render: (val) => format(parseFloat(val || 0).toFixed(2))
    },
    {
      title: "Paid Amount",
      dataIndex: "partialPaid",
      align: "right",
      render: (val) => format(parseFloat(val || 0).toFixed(2))
    },
    {
      title: "Balances Due Amount",
      dataIndex: "due",
      align: "right",
      render: (val) => format(parseFloat(val || 0).toFixed(2))
    },
    ...(authInfo?.branch_company_type === "Pharmacy" ? [
      {
        title: "Insurance Amount",
        dataIndex: "insurance_amount",
        align: "right",
        render: (val) => format(parseFloat(val || 0).toFixed(2))
      },
      {
        title: "Insurance Amount Paid",
        dataIndex: "insurance_amount_received",
        align: "right",
        render: (val) => format(parseFloat(val || 0).toFixed(2))
      }
    ] : [])
  ];

  // Calculate totals
  const totals = report.reduce((acc, row) => ({
    total_amount: acc.total_amount + parseFloat(row.total_amount || 0),
    paid_amount: acc.paid_amount + parseFloat(row.paid_amount || 0),
    partialPaid: acc.partialPaid + parseFloat(row.partialPaid || 0),
    due: acc.due + parseFloat(row.due || 0),
    insurance_amount: acc.insurance_amount + parseFloat(row.insurance_amount || 0),
    insurance_amount_received: acc.insurance_amount_received + parseFloat(row.insurance_amount_received || 0)
  }), {
    total_amount: 0, paid_amount: 0, partialPaid: 0, due: 0,
    insurance_amount: 0, insurance_amount_received: 0
  });

  if (printMode) {
  return (
    <table className="ledger-print-table">
      <thead>
        <tr className="ledger-print-heading">
          <td style={{ width: "6%" }}>No.</td>
          <td style={{ width: "18%" }}>Voucher No</td>

          <td style={{ width: "12%", textAlign: "right" }}>
            Bill Amount
          </td>

          <td style={{ width: "14%", textAlign: "right" }}>
            Paid Amount Invoice
          </td>

          <td style={{ width: "12%", textAlign: "right" }}>
            Paid Amount
          </td>

          <td style={{ width: "14%", textAlign: "right" }}>
            Balance Due Amount
          </td>

          {authInfo?.branch_company_type === "Pharmacy" && (
            <>
              <td style={{ width: "12%", textAlign: "right" }}>
                Insurance Amount
              </td>

              <td style={{ width: "12%", textAlign: "right" }}>
                Insurance Paid
              </td>
            </>
          )}
        </tr>
      </thead>

      <tbody>
        {report.map((row, index) => (
          <tr key={`${row.voucher_no}-${index}`}>
            <td>{index + 1}</td>

            <td>{row.voucher_no}</td>

            <td style={{ textAlign: "right" }}>
              {format(Number(row.total_amount || 0).toFixed(2))}
            </td>

            <td style={{ textAlign: "right" }}>
              {format(Number(row.paid_amount || 0).toFixed(2))}
            </td>

            <td style={{ textAlign: "right" }}>
              {format(Number(row.partialPaid || 0).toFixed(2))}
            </td>

            <td style={{ textAlign: "right" }}>
              {format(Number(row.due || 0).toFixed(2))}
            </td>

            {authInfo?.branch_company_type === "Pharmacy" && (
              <>
                <td style={{ textAlign: "right" }}>
                  {format(Number(row.insurance_amount || 0).toFixed(2))}
                </td>

                <td style={{ textAlign: "right" }}>
                  {format(
                    Number(row.insurance_amount_received || 0).toFixed(2)
                  )}
                </td>
              </>
            )}
          </tr>
        ))}

        <tr className="ledger-print-total">
          <td colSpan={2} style={{ textAlign: "right" }}>
            Grand Total :
          </td>

          <td style={{ textAlign: "right" }}>
            {format(totals.total_amount.toFixed(2))}
          </td>

          <td style={{ textAlign: "right" }}>
            {format(totals.paid_amount.toFixed(2))}
          </td>

          <td style={{ textAlign: "right" }}>
            {format(totals.partialPaid.toFixed(2))}
          </td>

          <td style={{ textAlign: "right" }}>
            {format(totals.due.toFixed(2))}
          </td>

          {authInfo?.branch_company_type === "Pharmacy" && (
            <>
              <td style={{ textAlign: "right" }}>
                {format(totals.insurance_amount.toFixed(2))}
              </td>

              <td style={{ textAlign: "right" }}>
                {format(totals.insurance_amount_received.toFixed(2))}
              </td>
            </>
          )}
        </tr>
      </tbody>
    </table>
  );
}

  return (
    <Table
      rowKey={(record, index) => `${record.voucher_no}-${index}`}
      columns={columns}
      dataSource={report}
      loading={loading}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
      }}
      onChange={handleTableChange}
      bordered
      scroll={printMode ? undefined : { x: 'max-content' }}
      size={printMode ? "small" : "middle"}
      data-testid="customer-due-table"
      summary={() => (
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={2}>
            <strong>Grand Total</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={2} align="right">
            <strong>{format(totals.total_amount.toFixed(2))}</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={3} align="right">
            <strong>{format(totals.paid_amount.toFixed(2))}</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={4} align="right">
            <strong>{format(totals.partialPaid.toFixed(2))}</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={5} align="right">
            <strong>{format(totals.due.toFixed(2))}</strong>
          </Table.Summary.Cell>

          {authInfo?.branch_company_type === "Pharmacy" && (
            <>
              <Table.Summary.Cell index={6} align="right">
                <strong>{format(totals.insurance_amount.toFixed(2))}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7} align="right">
                <strong>{format(totals.insurance_amount_received.toFixed(2))}</strong>
              </Table.Summary.Cell>
            </>
          )}
        </Table.Summary.Row>
      )}
    />
  );
};

export default CustomerDueTable;